import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Group, Stack } from '@mantine/core';
import { IconCancel as CancelIcon, IconDeviceFloppy as SaveIcon } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ZodSchema } from 'zod';

import { BaseIndicatorItemSchema } from '@/module/MinimalStandard/model/MinStdSchema';

import { Content, NumberInputControl, TextInputControl } from '@/core/components';
import { FileInputControl } from '@/core/components/Layout/Form/components/FileInputControl';

interface MinStdIndicatorItemFormProps<T extends BaseIndicatorItemSchema> {
  onChange: (request: T) => void;
  onCancel: () => void;

  defaultValues: DefaultValues<T>;

  isSubmitting: boolean;

  schema: ZodSchema<T>;

  renderExtraFields: (isSubmitting: boolean) => ReactNode;

  showPointsField: boolean;
}

export function BaseMinStdIndicatorItemForm<T extends BaseIndicatorItemSchema>(props: MinStdIndicatorItemFormProps<T>) {
  const { onChange, defaultValues, isSubmitting, onCancel, schema, renderExtraFields, showPointsField } = props;
  const { t } = useTranslation();

  const form = useForm<T>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  return (
    <Content type="full">
      <FormProvider {...form}>
        <Box
          flex={1}
          mb="10px"
          p="sm"
          component="form"
          onSubmit={form.handleSubmit(onChange)}
        >
          <Stack>
            {renderExtraFields(isSubmitting)}
            {showPointsField && (
              <NumberInputControl
                min={0}
                allowDecimal
                label={t('minStd.form.points')}
                name="points"
              />
            )}
            <Group>
              <TextInputControl
                name="link"
                label={t('minStd.form.link')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl
                name="note"
                label={t('minStd.form.note')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <FileInputControl
              name="newFile"
              label={t('minStd.form.file')}
              placeholder={defaultValues?.file?.title ?? t('minStd.form.file')}
              description={t('minStd.form.fileDescription')}
              isSubmitting={isSubmitting}
              accept="image/png,image/jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,application/vnd.rar,application/x-rar-compressed,application/x-gzip,application/x-7z-compressed"
            />
          </Stack>
          <Group
            mt="lg"
            justify="flex-end"
          >
            {onCancel && (
              <Button
                size="sm"
                leftSection={<CancelIcon />}
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t('button.cancel')}
              </Button>
            )}
            <Button
              size="sm"
              leftSection={<SaveIcon />}
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              {t('button.save')}
            </Button>
          </Group>
        </Box>
      </FormProvider>
    </Content>
  );
}
